export const DEMO_MEMBER_COUNT = 5;

export type DemoTransactionType = 'token-transfer' | 'contract-interaction';
export type DemoToken = 'ETH' | 'USDC';
export type DemoAsset = DemoToken;
export type DemoContractFunction = 'supply' | 'withdraw';

export const DEMO_DESTINATIONS = [
  'Approved vendor',
  'Service provider',
  'Operating wallet',
  'Team member',
] as const;

export type DemoTransferPolicy = {
  type: 'token-transfer';
  token: 'any' | DemoToken;
  maxAmount: number | null;
  destinations: readonly string[];
  requiredApprovals: number;
};
export type DemoContractPolicy = {
  type: 'contract-interaction';
  contract: 'lending-pool';
  function: DemoContractFunction;
  asset: 'any' | DemoAsset;
  amount: number | null;
  requiredApprovals: number;
};
export type DemoPolicy = DemoTransferPolicy | DemoContractPolicy;

type DemoRequestBase = { id: string; title: string; approvals: number };
export type DemoTransferRequest = DemoRequestBase & {
  type: 'token-transfer';
  token: DemoToken;
  amount: number;
  recipient: string;
};
export type DemoContractRequest = DemoRequestBase &
  (
    | {
        type: 'contract-interaction';
        contract: 'lending-pool' | 'other-pool';
        function: DemoContractFunction;
        args: { asset: DemoAsset; amount: number };
      }
    | {
        type: 'contract-interaction';
        contract: 'dai';
        function: 'approve';
        args: { spender: string; amount: number };
      }
  );
export type DemoRequest = DemoTransferRequest | DemoContractRequest;

export const DEFAULT_TRANSFER_POLICY: DemoTransferPolicy = {
  type: 'token-transfer',
  token: 'USDC',
  maxAmount: 5_000,
  destinations: DEMO_DESTINATIONS,
  requiredApprovals: 2,
};
export const DEFAULT_CONTRACT_POLICY: DemoContractPolicy = {
  type: 'contract-interaction',
  contract: 'lending-pool',
  function: 'supply',
  asset: 'USDC',
  amount: 5_000,
  requiredApprovals: 2,
};

const DEMO_TRANSFER_REQUESTS = [
  {
    id: 'transfer-vendor',
    title: 'Pay a vendor',
    type: 'token-transfer',
    token: 'USDC',
    amount: 1_250,
    recipient: 'Approved vendor',
    approvals: 3,
  },
  {
    id: 'transfer-large',
    title: 'Larger payment',
    type: 'token-transfer',
    token: 'USDC',
    amount: 7_500,
    recipient: 'Service provider',
    approvals: 5,
  },
  {
    id: 'transfer-eth',
    title: 'Send ETH',
    type: 'token-transfer',
    token: 'ETH',
    amount: 0.5,
    recipient: 'Operating wallet',
    approvals: 4,
  },
] as const satisfies readonly DemoTransferRequest[];

// These immutable samples cover different transaction rules and contract targets.
const SUPPLY_EXAMPLE: DemoContractRequest = {
  id: 'supply',
  title: 'Supply to a lending pool',
  type: 'contract-interaction',
  contract: 'lending-pool',
  function: 'supply',
  args: {
    asset: 'USDC',
    amount: 1_250,
  },
  approvals: 3,
};
const DEMO_CONTRACT_REQUESTS = [
  SUPPLY_EXAMPLE,
  {
    ...SUPPLY_EXAMPLE,
    id: 'withdraw-large',
    title: 'Larger withdrawal',
    function: 'withdraw',
    args: { ...SUPPLY_EXAMPLE.args, amount: 7_500 },
    approvals: 5,
  },
  {
    id: 'approve-dai',
    title: 'Approve DAI for a lending pool',
    type: 'contract-interaction',
    contract: 'dai',
    function: 'approve',
    args: { spender: 'Lending pool', amount: 2_500 },
    approvals: 4,
  },
] as const satisfies readonly DemoContractRequest[];

// Keep the mixed transfer and contract samples fixed while policy settings change.
export const DEMO_REQUESTS: readonly DemoRequest[] = [
  DEMO_TRANSFER_REQUESTS[0],
  DEMO_CONTRACT_REQUESTS[0],
  DEMO_TRANSFER_REQUESTS[1],
  DEMO_CONTRACT_REQUESTS[1],
  DEMO_TRANSFER_REQUESTS[2],
  DEMO_CONTRACT_REQUESTS[2],
];

export function formatDemoAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(
    amount
  );
}

export function demoContractName(
  contract: DemoContractRequest['contract']
): string {
  if (contract === 'lending-pool') return 'Lending pool';
  return contract === 'dai' ? 'DAI' : 'Another contract';
}

export function parseDemoAmount(value: string): number | null {
  if (!value.trim()) return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

export function demoRequestAmount(request: DemoRequest) {
  if (request.type === 'token-transfer')
    return { token: request.token, amount: request.amount };
  return {
    token: request.contract === 'dai' ? 'DAI' : request.args.asset,
    amount: request.args.amount,
  };
}

export function demoRequestArguments(request: DemoContractRequest) {
  if (request.contract === 'dai') {
    return [
      { name: 'spender', value: request.args.spender },
      { name: 'amount', value: `${formatDemoAmount(request.args.amount)} DAI` },
    ];
  }
  return [
    { name: 'asset', value: request.args.asset },
    {
      name: 'amount',
      value: `${formatDemoAmount(request.args.amount)} ${request.args.asset}`,
    },
  ];
}

function validAmount(amount: number | null): amount is number {
  return amount !== null && Number.isFinite(amount) && amount >= 0;
}

/**
 * Evaluate exactly one transaction type. Transfer rules never constrain contract
 * arguments, and contract arguments never constrain transfers. The supply/withdraw
 * examples check the target and function before interpreting their two arguments.
 * The calls are simplified examples rather than a real protocol ABI.
 */
export function evaluateDemoRequest(policy: DemoPolicy, request: DemoRequest) {
  const reasons: string[] = [];
  if (policy.type === 'token-transfer' && request.type === 'token-transfer') {
    // This demo uses an explicit allowlist: no checked destinations permits no transfers.
    if (!policy.destinations.includes(request.recipient)) {
      reasons.push(`Destination: ${request.recipient} is not allowed.`);
    }
    // Amount limits belong to one token; an Any-token policy has no shared cap.
    if (policy.token !== 'any') {
      if (request.token !== policy.token) {
        reasons.push(`Token: this policy allows ${policy.token}.`);
      } else if (!validAmount(policy.maxAmount)) {
        reasons.push('Enter a valid maximum token amount.');
      } else if (request.amount > policy.maxAmount) {
        reasons.push(
          `${formatDemoAmount(request.amount)} ${request.token} exceeds the ${formatDemoAmount(policy.maxAmount)} ${request.token} limit.`
        );
      }
    }
  } else if (
    policy.type === 'contract-interaction' &&
    request.type === 'contract-interaction'
  ) {
    if (request.contract !== policy.contract) {
      reasons.push('Contract: only the example lending pool is allowed.');
    } else if (request.function !== policy.function) {
      reasons.push(`Function: only ${policy.function}() is allowed.`);
    }
    // Only evaluate argument rules after the selected contract and function match.
    if (
      request.contract === policy.contract &&
      request.function === policy.function
    ) {
      const args = request.args;
      if (policy.asset !== 'any' && args.asset !== policy.asset) {
        reasons.push(
          `asset: expected ${policy.asset}, received ${args.asset}.`
        );
      }
      if (!validAmount(policy.amount)) {
        reasons.push('amount: enter a valid value.');
      } else if (args.amount > policy.amount) {
        reasons.push(
          `amount: ${formatDemoAmount(args.amount)} ${args.asset} exceeds the ${formatDemoAmount(policy.amount)} ${args.asset} limit.`
        );
      }
    }
  } else {
    reasons.push(
      policy.type === 'token-transfer'
        ? 'This policy only allows token transfers.'
        : 'This policy only allows contract interactions.'
    );
  }

  if (reasons.length)
    return { status: 'blocked' as const, label: 'Outside policy', reasons };
  if (request.approvals < policy.requiredApprovals) {
    const remaining = policy.requiredApprovals - request.approvals;
    return {
      status: 'awaiting' as const,
      label: 'Needs approvals',
      reasons: [
        `${remaining} more ${remaining === 1 ? 'approval' : 'approvals'} needed.`,
      ],
    };
  }
  return {
    status: 'ready' as const,
    label: 'Ready',
    reasons: [
      policy.requiredApprovals === 0
        ? 'Rules match. No member approvals required.'
        : 'Rules match and required approvals are present.',
    ],
  };
}
